/**
 * Service for checking event overlaps
 * File: src/services/EventOverlapService.js
 */
import axios from 'axios';
import { USER_ENDPOINTS } from '../Constants';

export const EventOverlapService = {
    /**
     * Check for overlapping events for a given date and time range
     * @param {string} date - Date in YYYY-MM-DD format
     * @param {string} startTime - Start time in HH:mm format
     * @param {string} endTime - End time in HH:mm format
     * @param {string} excludeEventId - Optional event ID to exclude from check (for editing)
     * @returns {Promise<Array>} - Array of overlapping events
     */
     checkForOverlaps: async (date, startTime, endTime, excludeEventId = null) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            // Get all events for the specified date
            const response = await axios.get(USER_ENDPOINTS.GET_EVENTS, {
                params: {
                    startDate: date,
                    endDate: date
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const events = response.data;

            // Convert time strings to comparable format
            const newEventStart = EventOverlapService.timeToMinutes(startTime);
            const newEventEnd = EventOverlapService.timeToMinutes(endTime);

            // Filter overlapping events
            const overlappingEvents = events.filter(event => {
                // Skip the event being edited
                if (excludeEventId && event.id === excludeEventId) {
                    return false;
                }

                // Skip if not the same date
                if (event.scheduledDate !== date) {
                    return false;
                }

                const existingStart = EventOverlapService.timeToMinutes(event.startTime);
                const existingEnd = EventOverlapService.timeToMinutes(event.endTime);

                // Check for overlap
                return EventOverlapService.doTimeRangesOverlap(
                    newEventStart, newEventEnd,
                    existingStart, existingEnd
                );
            });

            return overlappingEvents;
        } catch (error) {
            console.error('Error checking for event overlaps:', error);
            return [];
        }
    },

    /**
     * Convert time string (HH:mm) to minutes since midnight
     * @param {string} timeString - Time in HH:mm format
     * @returns {number} - Minutes since midnight
     */
    timeToMinutes: (timeString) => {
        if (!timeString) return 0;

        // Handle both HH:mm and HH:mm:ss formats
        const timeParts = timeString.split(':');
        const hours = parseInt(timeParts[0], 10) || 0;
        const minutes = parseInt(timeParts[1], 10) || 0;

        return hours * 60 + minutes;
    },

    /**
     * Check if two time ranges overlap
     * @param {number} start1 - Start time of first range (in minutes)
     * @param {number} end1 - End time of first range (in minutes)
     * @param {number} start2 - Start time of second range (in minutes)
     * @param {number} end2 - End time of second range (in minutes)
     * @returns {boolean} - True if ranges overlap
     */
    doTimeRangesOverlap: (start1, end1, start2, end2) => {
        // Two ranges overlap if:
        // - First range starts before second range ends AND
        // - Second range starts before first range ends
        return start1 < end2 && start2 < end1;
    },

    /**
     * Format overlapping events for display in warning message
     * @param {Array} overlappingEvents - Array of overlapping events
     * @returns {string} - Formatted string with event details
     */
    formatOverlappingEventsMessage: (overlappingEvents) => {
        if (!overlappingEvents || overlappingEvents.length === 0) {
            return '';
        }

        const eventsList = overlappingEvents.map(event => {
            const startTime = event.startTime ? event.startTime.substring(0, 5) : '00:00';
            const endTime = event.endTime ? event.endTime.substring(0, 5) : '00:00';

            let eventTitle = '';

            if (event.customerEntity) {
                // Client event
                const clientName = `${event.customerEntity.lastName} ${event.customerEntity.firstName}`;
                const eventTypeText = event.eventType === 'MONTAJ' ? 'Montaj' :
                    event.eventType === 'MASURATORI' ? 'Măsurătoare' :
                        event.eventType;
                eventTitle = `${eventTypeText} - ${clientName}`;
            } else {
                // Personal event
                eventTitle = `${event.description || 'Eveniment personal'}`;
            }

            return `• ${startTime}-${endTime}: ${eventTitle}`;
        }).join('\n');

        const pluralText = overlappingEvents.length === 1 ? 'eveniment' : 'evenimente';

        return `Atenție! Există ${overlappingEvents.length} ${pluralText} programat${overlappingEvents.length === 1 ? '' : 'e'} în această perioadă:\n\n${eventsList}`;
    }
};

export default EventOverlapService;